import React, {useMemo} from 'react'
import {Box, CircularProgress, IconButton, Tab, Tabs, Tooltip, Typography} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import {buildChartOptions} from 'src/components-new/data/chartOptions'
import {STATUS} from 'src/constants/constants'

interface QuestionnaireGraphItem {
  key: string
  label: string
  title: string
  notes?: string
  xAxis: string[]
  type: string
  series: Array<{name: string, data: number[], type: string}>
}

interface QuestionnaireChartCardProps {
  graphKey: string
  graphs: QuestionnaireGraphItem[]
  selectedTabKey?: string
  status?: string
  onTabChange: (graphKey: string, newValue: string) => void
}

function getAggregationTooltip(type?: string): string {
  if (type === 'date') {
    return 'Aggregazione basata sui mesi di calendario. \n Punteggio medio di tutti i pazienti mese per mese.'
  }
  if (type === 'single') {
    return 'Aggregazione basata sui tempi di compilazione. \n T0 è la media di tutte le prime compilazioni del questionario di tutti i pazienti, \n T1 è la media di tutte le seconde compilazioni e così via.'
  }
  if (type === 'interval') {
    return "Aggregazione basata sull'intervallo di compilazione. \n T0 è la media di tutte le compilazioni a partire dalla prima a + 30 giorni, \n T1 è la media di tutte le compilazioni a partire da T0 + 30 giorni fino a T0 + 60 giorni e così via."
  }
  return type ?? ''
}

// Chart accent color for questionnaire graphs — a Highcharts series color (data
// visualization), not a themeable UI surface, kept identical to legacy Home.tsx's
// <Chart colors={['#4472C4']} .../> for the same QuestionnaireChart panels.
const QUESTIONNAIRE_CHART_COLOR = '#4472C4'

// Porting of the local `QuestionnaireChart` component in src/pages/home/Home.tsx
// (one Tabs-driven chart card per dynamic questionnaire, tab = compilation).
export default function QuestionnaireChartCard({
  graphKey,
  graphs,
  selectedTabKey,
  status,
  onTabChange,
}: QuestionnaireChartCardProps): JSX.Element | null {
  const selectedGraph = useMemo(() => (
    Array.isArray(graphs) ? graphs.find(g => g.key === selectedTabKey) : undefined
  ), [graphs, selectedTabKey])

  const chartData = useMemo(() => {
    if (!selectedGraph) return null

    const data = JSON.parse(JSON.stringify(selectedGraph))
    const seriesData = data.series?.[0]?.data
    const xAxisData = data.xAxis

    if (Array.isArray(seriesData) && seriesData.length > 12) {
      const startIndex = seriesData.length - 12
      data.series[0].data = seriesData.slice(startIndex)
      if (Array.isArray(xAxisData)) {
        data.xAxis = xAxisData.slice(startIndex)
      }
    }
    return data
  }, [selectedGraph])

  if (!graphs || !Array.isArray(graphs) || graphs.length === 0) {
    return null
  }

  return (
    <Card
      sx={{gridColumn: '1 / -1'}}
      title={
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Typography variant="h3">{selectedGraph?.title}</Typography>
          <Tooltip title={getAggregationTooltip(selectedGraph?.type)}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      }
    >
      <Box sx={{borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center'}}>
        <Tabs
          value={selectedTabKey || false}
          onChange={(_e, newValue) => onTabChange(graphKey, newValue)}
          scrollButtons="auto"
          variant="scrollable"
        >
          {graphs.map(graph => (
            <Tab key={graph.key} label={graph.label} value={graph.key} />
          ))}
        </Tabs>
        {selectedGraph?.notes && (
          <Tooltip title={selectedGraph.notes}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {chartData ? (
        <Box sx={{padding: '20px 0'}}>
          {status === STATUS.PENDING ? (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
              <CircularProgress />
            </Box>
          ) : (
            <HighchartsReact
              highcharts={Highcharts}
              options={buildChartOptions({type: 'column', data: chartData, colors: [QUESTIONNAIRE_CHART_COLOR], legend: false})}
            />
          )}
        </Box>
      ) : (
        <EmptyState title="Non ci sono statistiche per questo grafico" />
      )}
    </Card>
  )
}
